import {NameTemplatePartNode, NameTemplatePartType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getNameTemplatePartTypeToString, NameTemplatePart} from 'name-templates/NameTemplateConstants';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {OptionData} from 'ui-library/menus/MenuConstants';
import runModal from 'ui-library/runModal';
import * as Modals from 'util/Modals';

function toNameTemplatePart(node: NameTemplatePartNode): NameTemplatePart {
  const {type, fieldId, text, datetimeFormat, displayName, exampleValue, typeSpecificConfiguration} = node;
  return {
    type,
    ...(fieldId && {fieldId}),
    ...(text && {text}),
    ...(datetimeFormat && {datetimeFormat}),
    ...(displayName && {displayName}),
    ...(exampleValue && {exampleValue}),
    ...(typeSpecificConfiguration && {typeSpecificConfiguration}),
  } as NameTemplatePart;
}

export function getOptionsForNameTemplatePartType(
  schemaFields: Array<SchemaField | ClientSchemaField>,
  possibleNameTemplateParts: Array<NameTemplatePartNode>,
  appendPartFunc: (part: NameTemplatePart) => void,
  selectedNamePartType: NameTemplatePartType
): Array<OptionData> {
  const options: Array<OptionData> = [];

  for (const nameTemplatePart of possibleNameTemplateParts) {
    if (nameTemplatePart.type !== selectedNamePartType) {
      continue;
    }

    const displayName = getNameTemplatePartTypeToString(toNameTemplatePart(nameTemplatePart));

    // Handle special cases that need modal input
    switch (nameTemplatePart.type) {
      case NameTemplatePartType.TEXT:
        options.push({
          name: displayName,
          value: async () => {
            const text = await runModal(Modals.PromptModal, {
              enableRedesign: true,
              title: 'Enter a value',
              initialValue: '',
              validate: (s: string) => s.length > 0,
              medium: true,
            });
            if (text) {
              appendPartFunc({type: NameTemplatePartType.TEXT, text});
            }
          },
        });
        break;

      case NameTemplatePartType.SEPARATOR:
        options.push({
          name: displayName,
          value: async () => {
            const text = await runModal(Modals.PromptModal, {
              enableRedesign: true,
              title: 'Enter a separator value',
              initialValue: '',
              validate: (s: string) => s.length > 0,
              medium: true,
            });
            if (text) {
              appendPartFunc({type: NameTemplatePartType.SEPARATOR, text});
            }
          },
        });
        break;

      case NameTemplatePartType.LINKED_BIOENTITY_REGISTRY_IDENTIFIER:
      case NameTemplatePartType.CHILD_ENTITY_LOT_NUMBER:
      case NameTemplatePartType.COMPLEX_POLYMER_COMPONENT: {
        const field = schemaFields.find((f) => f.apiId === nameTemplatePart.fieldId);
        const name = field ? `${displayName}: ${field.name}` : displayName;
        options.push({
          name,
          value: () => {
            appendPartFunc(toNameTemplatePart(nameTemplatePart));
          },
        });
        break;
      }

      default: {
        const name = nameTemplatePart.fieldId
          ? (schemaFields.find((f) => f.apiId === nameTemplatePart.fieldId)?.name ?? displayName)
          : displayName;
        options.push({
          name,
          value: () => {
            appendPartFunc(toNameTemplatePart(nameTemplatePart));
          },
        });
        break;
      }
    }
  }

  return options;
}
